/**
 * Stable machine error codes — the registry contract guidelines §2 makes
 * authoritative ("新增 code 必须先在 api-contract 登记"). Frontends branch
 * ONLY on `error.code`, never on message text; the Go backend mirrors this
 * registry (backend/internal/httpapi/errors.go) and must not emit a code
 * that is not registered here first.
 *
 * Adding a code: extend the matching domain list + ERROR_CODE_META, note
 * the emitting endpoint(s) in its doc comment, and bump
 * API_CONTRACT_VERSION (minor). Codes are never renamed or removed inside
 * a major version — only deprecated in comments.
 */
/** HTTP statuses the contract uses, including the non-standard 419. */
export const HTTP_STATUS = {
    OK: 200,
    CREATED: 201,
    ACCEPTED: 202,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_ALLOWED: 405,
    CONFLICT: 409,
    /** Contract-fixed CSRF failure status (guidelines §4): refresh once, retry once. */
    CSRF_FAILED: 419,
    TOO_MANY_REQUESTS: 429,
    INTERNAL_ERROR: 500,
    /** Upstream Mojang/proxy failure (register premium chain). */
    BAD_GATEWAY: 502,
    /** Unavailable backend capability (AI provider / bridge not configured). */
    SERVICE_UNAVAILABLE: 503,
};
/**
 * Infrastructure codes — transport/framework-level, any endpoint may emit
 * them. Kept in sync with backend/internal/httpapi (the Go side is not
 * allowed to grow this list on its own; register here first).
 */
export const INFRA_ERROR_CODES = [
    /** Malformed JSON / wrong content type / unreadable request. */
    'bad_request',
    /** Field rules failed; `error.fields` carries per-field messages. */
    'validation_failed',
    /** No session, or session expired/revoked. Frontend: to login. */
    'unauthorized',
    /** Authenticated but not allowed (incl. Origin allowlist rejection). */
    'forbidden',
    /** Unknown resource; also used for admin-only routes hit by non-admins. */
    'not_found',
    'method_not_allowed',
    /** State conflict (e.g. a uniqueness race lost at write time). */
    'conflict',
    /** CSRF token missing/invalid/expired — HTTP 419, fixed by guidelines §4. */
    'csrf_failed',
    /** Client-side throttle hit (login attempts, mail sends, ...). */
    'too_many_requests',
    'internal_error',
];
/**
 * Auth-domain business codes (phase 4.1). Emitted only by /api/v1/auth/*
 * unless a later registration explicitly widens a code's scope.
 */
export const AUTH_ERROR_CODES = [
    /**
     * The captcha answer was wrong (or the challenge expired/was consumed).
     * The challenge is burned on every verify attempt, so the frontend MUST
     * fetch a fresh one (GET /api/v1/auth/captcha) before resubmitting.
     * `error.fields.captcha` carries the user-visible message.
     */
    'captcha_failed',
    /**
     * The signed registration continuation link (u/h/p) is missing,
     * malformed, expired or fails signature validation. Frontend: abandon
     * the form and restart from the verification-mail request.
     */
    'registration_link_invalid',
    /**
     * Mojang API (or the mc_spider proxy in front of it) failed: 5xx,
     * connection error, or an unusable texture blob. Emitted by
     * /api/v1/auth/register/stamped-skin, /register/skin-check and
     * POST /register (HTTP 502). Frontend: retryable, never a field error.
     */
    'mojang_unavailable',
];
/**
 * Business codes promoted from the A2 workstream (chat + workspace-validate
 * domains). The code constant and the emitting endpoint doc live in the
 * domain module; they register here so ErrorCode stays the closed union.
 */
export const A2_ERROR_CODES = [
    /** POST /api/v1/chat with no AI provider configured — 503, see v1/chat. */
    'ai_unavailable',
    /** POST /api/v1/workspaces/{id}/validate with no bridge configured — 503, see v1/workspace. */
    'bridge_unavailable',
];
/** Every registered code. Union grows per business phase, one list per domain. */
export const ERROR_CODES = [...INFRA_ERROR_CODES, ...AUTH_ERROR_CODES, ...A2_ERROR_CODES];
/**
 * Per-code metadata. `httpStatus` is contractual (the Go side MUST answer
 * with it). `summary` is developer documentation ONLY — the user-visible
 * `error.message` is always server-owned zh-CN copy and must never be
 * reconstructed client-side from this table.
 */
export const ERROR_CODE_META = {
    bad_request: { httpStatus: HTTP_STATUS.BAD_REQUEST, summary: '请求无法解析' },
    validation_failed: {
        httpStatus: HTTP_STATUS.BAD_REQUEST,
        summary: '字段校验失败,见 error.fields',
    },
    unauthorized: { httpStatus: HTTP_STATUS.UNAUTHORIZED, summary: '未登录或会话失效' },
    forbidden: { httpStatus: HTTP_STATUS.FORBIDDEN, summary: '已登录但无权限 / Origin 被拒' },
    not_found: { httpStatus: HTTP_STATUS.NOT_FOUND, summary: '资源不存在(或非管理员访问管理路由)' },
    method_not_allowed: {
        httpStatus: HTTP_STATUS.METHOD_NOT_ALLOWED,
        summary: '方法不允许(写操作禁止 GET)',
    },
    conflict: { httpStatus: HTTP_STATUS.CONFLICT, summary: '与当前状态冲突(写入竞争)' },
    csrf_failed: {
        httpStatus: HTTP_STATUS.CSRF_FAILED,
        summary: 'CSRF 校验失败,只允许刷新 token 一次并重试一次',
    },
    too_many_requests: {
        httpStatus: HTTP_STATUS.TOO_MANY_REQUESTS,
        summary: '触发限流(登录尝试/邮件发送等),按字段消息等待',
    },
    internal_error: { httpStatus: HTTP_STATUS.INTERNAL_ERROR, summary: '服务器内部错误' },
    captcha_failed: {
        httpStatus: HTTP_STATUS.BAD_REQUEST,
        summary: '验证码错误或已失效,必须重新获取验证码',
    },
    registration_link_invalid: {
        httpStatus: HTTP_STATUS.BAD_REQUEST,
        summary: '注册续链(u/h/p)无效或过期,需重新请求验证邮件',
    },
    mojang_unavailable: {
        httpStatus: HTTP_STATUS.BAD_GATEWAY,
        summary: 'Mojang 服务暂不可用(注册正版链路的皮肤/UUID 查询),可重试',
    },
    ai_unavailable: {
        httpStatus: HTTP_STATUS.SERVICE_UNAVAILABLE,
        summary: 'AI provider 未配置(GO_AI_BASE_URL/GO_AI_API_KEY),聊天不可用',
    },
    bridge_unavailable: {
        httpStatus: HTTP_STATUS.SERVICE_UNAVAILABLE,
        summary: 'everbridge 未配置或不可达,无法触发引擎校验',
    },
};
//# sourceMappingURL=error-codes.js.map